"use client";

import React, { useMemo } from "react";

import type { T_ChartData } from "@lattice/core-lib/types/chart";
import { formatPortfolioScrubTimestamp } from "@lattice/shared-frontend/charts";

import { LineChart } from "@lattice/frontend/components/charts-kit/LineChart";

import type { T_PortfolioHomeChartPoint } from "./types";
import "./portfolio-home.css";

export type T_PortfolioChartProps = {
  isPositive: boolean;
  onScrubEnd?: () => void;
  onScrubPoint?: (point: T_PortfolioHomeChartPoint) => void;
  points: T_PortfolioHomeChartPoint[];
};

export function PortfolioChart({
  isPositive,
  onScrubEnd,
  onScrubPoint,
  points,
}: T_PortfolioChartProps) {
  const chart = useMemo<T_ChartData>(
    () => ({
      data: points.map((point) => ({
        label: formatPortfolioScrubTimestamp(point.timestamp),
        value: point.value,
      })),
      type: "line",
      valueFormat: "currency",
    }),
    [points],
  );

  if (points.length < 2) {
    return (
      <div className="portfolio-home-chart portfolio-home-chart--empty">
        Portfolio history will appear after your first sync.
      </div>
    );
  }

  return (
    <div className="portfolio-home-chart" data-testid="portfolio-home-chart">
      <LineChart
        chart={chart}
        color={
          isPositive
            ? "var(--lattice-financial-positive)"
            : "var(--lattice-financial-down)"
        }
        height={190}
        onScanPointChange={(point) => {
          if (!point) {
            onScrubEnd?.();
            return;
          }
          const sourcePoint = points[point.index];
          if (sourcePoint) onScrubPoint?.(sourcePoint);
        }}
        showHeader={false}
        syntheticFallback={onScrubPoint === undefined}
      />
    </div>
  );
}
